import { useReveal } from 'features/libra/comments/CommentsProvider'
import {
  ResizableNPanelLayout,
  ResizableNPanelLayoutPanel,
} from 'features/libra/components/ResizableNPanelLayout'
import { useStepNavigation } from 'features/libra/StepNavigationContext'
import { WorkflowScopedProviders } from 'features/libra/workflow/WorkflowScopedProviders'
import { WORKSPACE_PANEL_REGISTRY } from 'features/libra/workspacePanels'
import { FC, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import styled from 'styled-components'

import { WorkspaceLayoutConfig } from './workspaceLayoutTypes'

const Empty = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ErrorDetail = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  max-width: 32rem;
  white-space: pre-wrap;
  word-break: break-word;
`

const PanelErrorFallback: FC<{ panelName: string; error: Error }> = ({
  panelName,
  error,
}) => (
  <Empty>
    <div>
      Panel &quot;{panelName}&quot; failed to render.
      <ErrorDetail>
        {(error.message ?? String(error)).slice(0, 500)}
      </ErrorDetail>
    </div>
  </Empty>
)

interface WorkspaceLayoutRendererProps {
  layout: WorkspaceLayoutConfig
}

/**
 * Renders a resolved `WorkspaceLayoutConfig` against the panel registry.
 * Workflow-specific workspace providers (e.g. FFS workspace state, so
 * sibling panels within a step can share selection) are injected here via
 * the workflow-scoped provider registry -- per workspace render, below the
 * step key and tab remount boundaries.
 */
export const WorkspaceLayoutRenderer: FC<WorkspaceLayoutRendererProps> = ({
  layout,
}) => {
  const { activeStep } = useStepNavigation()

  const resolvedPanels = useMemo<ReadonlyArray<ResizableNPanelLayoutPanel>>(
    () =>
      layout.panels.map((panel, idx) => {
        const Comp = WORKSPACE_PANEL_REGISTRY[panel.component]
        // Prefer the schema-declared `panel.id` (stable across reorder /
        // config edits) and fall back to the synthesized
        // `${component}-${idx}` only when older fixtures omit it. The
        // synthesized form remounts every panel after the change point on
        // any reorder, which breaks per-panel state.
        // `weight`/`minWidthRem` are defaulted by the Zod schema at parse
        // time and `component` is a schema enum keyed into the registry --
        // trust the parse boundary rather than re-defaulting here.
        return {
          id: panel.id ?? `${panel.component}-${idx}`,
          component: panel.component,
          weight: panel.weight,
          minWidthRem: panel.minWidthRem,
          collapseGroup: panel.collapseGroup,
          content: (
            <ErrorBoundary
              fallbackRender={({ error }) => (
                <PanelErrorFallback panelName={panel.component} error={error} />
              )}
              resetKeys={[activeStep?.id, panel.component]}>
              <Comp />
            </ErrorBoundary>
          ),
        }
      }),
    [layout, activeStep?.id]
  )

  // Following a link to a zone comment selects the zone's reference image,
  // so re-open the document panels if the user had collapsed them.
  const [expandSignal, setExpandSignal] = useState(0)
  useReveal((anchor) => {
    if (anchor.stepId === activeStep?.id && 'zoneId' in anchor)
      setExpandSignal((n) => n + 1)
  })

  if (!activeStep) {
    return <Empty>No active step for this workflow.</Empty>
  }

  return (
    <WorkflowScopedProviders placement='workspace'>
      <ResizableNPanelLayout
        panels={resolvedPanels}
        expandSignal={expandSignal}
      />
    </WorkflowScopedProviders>
  )
}
